import { Injectable } from '@angular/core';

import { ApiError, MessageService } from '../../../../core';
import { DecimalFormatPipe } from '../../../../shared';
import { PedidoIsLiberadoEntitier, PedidoIsLiberadoRequest } from '../adapters';
import { PedidoNaoLiberadoHttpService } from './pedido-nao-liberado-http.service';
import { isPedidoMotivoNaoLiberadoBonificacaoExcedente, isPedidoMotivoNaoLiberadoPercDescontosMaximosItensExcedidos, isPedidoMotivoNaoLiberadoClienteInadimplente, isPedidoMotivoNaoLiberadoLimiteCreditoExcedido } from '../domain';
import { pedidoNaoLiberado } from '../../status';

@Injectable({
    providedIn: 'root'
})
export class PedidoIsLiberadoService {

    constructor(private messageService: MessageService,
                private decimalFormatPipe: DecimalFormatPipe,
                private pedidoNaoLiberadoHttpService: PedidoNaoLiberadoHttpService) { }

    /** Confirma a liberação ou não de um pedido conforme as regras comerciais */
    confirmIsLiberado(entity: PedidoIsLiberadoEntitier, createReq: boolean, tipoPedidoSinomimo = 'Pedido'): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            // Validando pedido no server
            const req = createReq ? new PedidoIsLiberadoRequest(entity) : null;
            this.pedidoNaoLiberadoHttpService.isLiberado(entity?.id ?? 0, req)
                .subscribe(
                    res => {
                        if (res.liberado) { // Pedido liberado
                            resolve(true);
                        } else { // Pedido não liberado
                            if (isPedidoMotivoNaoLiberadoBonificacaoExcedente(res.motivoNaoLiberado)) { // Bonificação excedente
                                this.messageService.confirm(`O percentual de ${this.decimalFormatPipe.transform(res.percBonificacao, 1, 2, 2)}% desta bonificação excedeu o percentual limite de ${this.decimalFormatPipe.transform(res.percBonificacaoMaxima, 1, 2, 2)}% da campanha selecionada.\n\nVocê pode salvar o ${tipoPedidoSinomimo} e o mesmo ficará como "Não Liberado" precisando ser aprovado posteriormente ou não salvar o ${tipoPedidoSinomimo} e alterar os valores bonificados.\n\nDeseja salvar o ${tipoPedidoSinomimo}?`, 'Confirme').then(result => {
                                    if (result) {
                                        entity.status = pedidoNaoLiberado.id;
                                        entity.motivoNaoLiberado = res.motivoNaoLiberado;
                                        entity.dataNaoLiberado = res.dataNaoLiberado;
                                        entity.valorRegraNaoLiberado = res.valorRegraNaoLiberado;
                                        entity.tipoRegraBonificacaoNaoLiberado = res.tipoRegraBonificacaoNaoLiberado;
                                        entity.valorBonificacaoNaoLiberado = res.valorBonificacao;
                                        entity.percBonificacaoNaoLiberado = res.percBonificacao;
                                        entity.percBonificacaoMaximaNaoLiberado = res.percBonificacaoMaxima;
                                        entity.dataLiberado = res.dataLiberado;
                                        entity.idUsuarioLiberado = res.idUsuarioLiberado;
                                        entity.justificativaLiberado = res.justificativaLiberado;
                                        resolve(true);
                                    } else {
                                        resolve(false);
                                    }
                                });
                            } else if (isPedidoMotivoNaoLiberadoPercDescontosMaximosItensExcedidos(res.motivoNaoLiberado)) { // Percentuais de descontos dos itens acima do limite permitido
                                this.messageService.confirm(`Este ${tipoPedidoSinomimo} possui itens com descontos acima do limite permitido.\n\nVocê pode salvar o ${tipoPedidoSinomimo} e o mesmo ficará como "Não Liberado" precisando ser aprovado posteriormente ou não salvar o ${tipoPedidoSinomimo} e alterar os descontos excedentes.\n\nDeseja salvar o ${tipoPedidoSinomimo}?`, 'Confirme').then(result => {
                                    if (result) {
                                        entity.status = pedidoNaoLiberado.id;
                                        entity.motivoNaoLiberado = res.motivoNaoLiberado;
                                        entity.dataNaoLiberado = res.dataNaoLiberado;

                                        entity.dataLiberado = res.dataLiberado;
                                        entity.idUsuarioLiberado = res.idUsuarioLiberado;
                                        entity.justificativaLiberado = res.justificativaLiberado;
                                        resolve(true);
                                    } else {
                                        resolve(false);
                                    }
                                });
                            } else if (isPedidoMotivoNaoLiberadoClienteInadimplente(res.motivoNaoLiberado)) { // Cliente inadimplente (possui títulos vencidos em aberto)
                                this.messageService.confirm(`Este ${tipoPedidoSinomimo} não pode ser liberado pois o cliente possui títulos vencidos e está inadimplente.\n\nVocê pode salvar o ${tipoPedidoSinomimo} e o mesmo ficará como "Não Liberado" precisando ser aprovado posteriormente ou salvar como Não Confirmado e resolver as pendências financeiras do cliente.\n\nDeseja salvar o ${tipoPedidoSinomimo}?`, 'Confirme').then(result => {
                                    if (result) {
                                        entity.status = pedidoNaoLiberado.id;
                                        entity.motivoNaoLiberado = res.motivoNaoLiberado;
                                        entity.dataNaoLiberado = res.dataNaoLiberado;

                                        entity.quantidadeTitulosVencidosNaoLiberado = res.quantidadeTitulosVencidosNaoLiberado;
                                        entity.valorTitulosVencidosNaoLiberado = res.valorTitulosVencidosNaoLiberado;
                                        entity.titulosVencidosNaoLiberado = res.titulosVencidosNaoLiberado;

                                        entity.dataLiberado = res.dataLiberado;
                                        entity.idUsuarioLiberado = res.idUsuarioLiberado;
                                        entity.justificativaLiberado = res.justificativaLiberado;
                                        resolve(true);
                                    } else {
                                        resolve(false);
                                    }
                                });
                            } else if (isPedidoMotivoNaoLiberadoLimiteCreditoExcedido(res.motivoNaoLiberado)) { // Valor total ultrapassou o limite de crédito disponível do cliente
                                this.messageService.confirm(`O valor total deste ${tipoPedidoSinomimo} ultrapassou o limite de crédito disponível do cliente.\n\nVocê pode salvar o ${tipoPedidoSinomimo} e o mesmo ficará como "Não Liberado" precisando ser aprovado posteriormente ou salvar como Não Confirmado e ajustar os valores.\n\nDeseja salvar o ${tipoPedidoSinomimo}?`, 'Confirme').then(result => {
                                    if (result) {
                                        entity.status = pedidoNaoLiberado.id;
                                        entity.motivoNaoLiberado = res.motivoNaoLiberado;
                                        entity.dataNaoLiberado = res.dataNaoLiberado;

                                        entity.limiteCreditoDisponivelNaoLiberado = res.limiteCreditoDisponivelNaoLiberado;
                                        entity.valorEmAbertoNaoLiberado = res.valorEmAbertoNaoLiberado;
                                        entity.saldoLimiteCreditoNaoLiberado = res.saldoLimiteCreditoNaoLiberado;
                                        entity.pedidosEmAbertoNaoLiberado = res.pedidosEmAbertoNaoLiberado;

                                        entity.dataLiberado = res.dataLiberado;
                                        entity.idUsuarioLiberado = res.idUsuarioLiberado;
                                        entity.justificativaLiberado = res.justificativaLiberado;
                                        resolve(true);
                                    } else {
                                        resolve(false);
                                    }
                                });
                            } else { // Outro (Retornamos pedido válido)
                                resolve(true);
                            }
                        }
                    },
                    (error: ApiError) => {
                        this.messageService.error(error.message, `Erro ao verificar se o ${tipoPedidoSinomimo} está liberado ou não conforme as regras comerciais`, error);
                        reject(false);
                    }
                );
        });
    }
}
